// Art/perspective prototype: the scenes in common/data/hd-scenes/ (a region of the map each) painted at 2x density.
// This file owns the ground, lights and street furniture; building kinds, roofs and roof props are registered by
// hd-fronts-<region>.ts (hd-registry.ts, hd-building.ts). Three-quarter view like the concept boards: roads are screen-aligned, roofs are seen from above and every
// building shows its south wall inside its own solid tiles, so scenery never covers walkable ground. People and
// street furniture that stand up (lamps, signal poles, trees, bollards) are returned as props the renderer sorts
// by their feet. Presentation only: nothing here changes collision, shops or zones.
import { CHALLENGES, CITY_ART, CONFIG, HD_SCENES, type HdScene, type Rect, WORLD } from '../../common/src/data';
import worldEditsJson from '../../common/data/world-edits.json';
import { readEdits, worldEditsProblems, type WorldEdits } from '../../common/src/world-edits';
import { worldPropSprite } from './world-prop-sprite';
import { GameMap, T, TILE, tileAt, inTileRect as inRect, boothFootprint, boothFeet } from '../../common/src/map';
import { canvas } from './canvas';
import { hash2 } from '../../common/src/util';
import { HT, K, type G, px, speckle, dither, bands, disc, frame, shadow, dk, lt, mix, alpha, text, textW } from './hd-kit';
import { poiColor } from './city-art';
import type { LightFn } from './hd-registry';
import { paintBuilding } from './hd-building';
import { awning, lantern } from './hd-parts';
import { helipadPad, helipadFence, windsock, beacon } from './hd-helipad';
import { paintHotelMark } from './hd-hotel';
import { paintGunsmith } from './hd-place-gunsmith';
import { paintBroker } from './hd-place-broker';
import { paintClub, isClubTile } from './hd-place-club';
import { paintExhaust, exhaustProps } from './hd-place-exhaust';
import { paintPaper9, paper9Props } from './hd-place-paper9';
import { paintKillSwitch, killSwitchProps } from './hd-place-killswitch';
import { paintHachiGround } from './hd-hachi-study';
import { paintNeighborhoodGround } from './hd-hachi-neighborhood';
import { paintKabukiEastGround } from './hd-kabuki-east-study';
import { placeArt, placeGround } from './hd-place-shops';
import { isDeckLow, deckFence, paintDeck, deckProps } from './hd-place-carpark';
import { paintRobotKioskDecal } from './hd-fronts-kabuki-north';
import { setSanctuaryOrigin } from './hd-sanctuary';
import { paintSanctuary, sanctuaryProps, inSanctuary } from './hd-sanctuary-place';

/** A light in the scene, in world px. refl: cast a wet reflection on walkable ground below it; pool: light the ground
 *  around it; live: stays a flickering sprite (neon signs). Everything else is baked into the scene image. */
export interface HdLight { x: number; y: number; w: number; h: number; color: string; alpha: number; refl?: boolean; pool?: boolean; live?: boolean }
/** An upright prop: canvas in hd px, base point (feet) in world px, anchor in canvas px. */
export interface HdProp { canvas: HTMLCanvasElement; x: number; y: number; ax: number; ay: number }
/** What the renderer keeps of a painted scene (the ground image itself goes out through `onPiece`). */
export interface HdSceneArt { id: string; x: number; y: number; w: number; h: number; lights: HdLight[]; /** every light incl. the baked ones (lamps, vending): sources for lighting.ts */ all: HdLight[]; props: HdProp[] }

/** World Studio props (common/data/world-edits.json), read and checked once per map. A file that does not read or
 *  breaks the ground or budget rules is skipped whole with a console warning: the city must still paint. */
const worldEditsCache = new WeakMap<GameMap, WorldEdits | null>();
function loadWorldEdits(m: GameMap): WorldEdits | null {
  if (worldEditsCache.has(m)) return worldEditsCache.get(m)!;
  let edits: WorldEdits | null = null;
  try {
    const e = readEdits(worldEditsJson, m);
    const problems = worldEditsProblems(e, m, HD_SCENES.map(s => ({ id: s.id, ...s.rect })), CONFIG.worldProps);
    if (problems.length) console.warn('world-edits.json skipped:', problems.slice(0, 5).join(' '));
    else if (e.props.length) edits = e;
  } catch (err) { console.warn('world-edits.json skipped:', err instanceof Error ? err.message : err); }
  worldEditsCache.set(m, edits);
  return edits;
}

/** largest canvas painted at once, in hd px; a bigger scene is painted as several pieces */
export const MAX_PIECE = 1024;
/** the scene being painted (module state: painting is synchronous, one scene at a time) */
let R: Rect = HD_SCENES[0].rect;
/** tile range (x1, y1 exclusive) of the piece being painted, plus a 2-tile margin: ground loops stay inside it */
let C = { x0: R.x, y0: R.y, x1: R.x + R.w, y1: R.y + R.h };
/** the piece in scene hd px (lights outside it are not baked); null = whole scene */
let PB: { x0: number; y0: number; x1: number; y1: number } | null = null;
/** open ground that lights and reflections may land on (not bollards, stalls or walls) */
export const walkable = (t: T) => t === T.ROAD || t === T.SIDEWALK || t === T.ALLEY || t === T.PLAZA;

/** One piece of a scene's ground image: `ox, oy, w, h` in scene hd px, `x, y, ww, wh` its place in world px. */
export interface HdPiece { scene: HdScene; ox: number; oy: number; w: number; h: number; x: number; y: number; ww: number; wh: number }

/** Cut a scene into pieces of at most MAX_PIECE x MAX_PIECE hd px (whole tiles); scenes up to 64x64 tiles are one piece.
 *  The renderer paints pieces when the camera comes near, since painting the whole map at once takes seconds. */
export function hdPieces(S: HdScene): HdPiece[] {
  const r = S.rect, out: HdPiece[] = [];
  const nx = Math.ceil(r.w * HT / MAX_PIECE), ny = Math.ceil(r.h * HT / MAX_PIECE);
  const pw = Math.ceil(r.w / nx) * HT, ph = Math.ceil(r.h / ny) * HT;
  for (let oy = 0; oy < r.h * HT; oy += ph) for (let ox = 0; ox < r.w * HT; ox += pw) {
    const w = Math.min(pw, r.w * HT - ox), h = Math.min(ph, r.h * HT - oy);
    out.push({ scene: S, ox, oy, w, h, x: r.x * TILE + ox / 2, y: r.y * TILE + oy / 2, ww: w / 2, wh: h / 2 });
  }
  return out;
}

/** Paint one piece. It replays the whole scene through a translated context, so it looks exactly like the same area of
 *  one big canvas; the returned lights and props are the whole scene's (the same for every piece). */
export function paintHdPiece(m: GameMap, p: HdPiece): { canvas: HTMLCanvasElement; art: HdSceneArt } {
  const job = startHdPiece(m, p); job.run(Infinity); return { canvas: job.canvas, art: job.art! };
}

/** A piece painted a slice at a time: run(ms) paints for about ms milliseconds and returns true when the piece is done
 *  (then rt is set). Only one job may be in progress at a time (the painters share module state). */
export function startHdPiece(m: GameMap, p: HdPiece, includeEdits = true) {
  const [canvas, g] = canvas0(p);
  const steps = paintScene(m, p.scene, g, includeEdits);
  const job = {
    canvas, art: undefined as HdSceneArt | undefined,
    run(ms: number) {
      if (job.art) return true;
      setPiece(p);
      const end = performance.now() + ms;
      do { const s = steps.next(); if (s.done) { job.art = s.value; return true; } } while (performance.now() < end);
      return false;
    },
  };
  return job;
}
function canvas0(p: HdPiece) {
  const [c, g] = canvas(p.w, p.h); g.translate(-p.ox, -p.oy); return [c, g] as const;
}
function setPiece(p: HdPiece) {
  R = p.scene.rect;
  PB = { x0: p.ox, y0: p.oy, x1: p.ox + p.w, y1: p.oy + p.h };
  C = { x0: Math.max(R.x, R.x + Math.floor(p.ox / HT) - 2), y0: Math.max(R.y, R.y + Math.floor(p.oy / HT) - 2), x1: Math.min(R.x + R.w, R.x + Math.ceil((p.ox + p.w) / HT) + 2), y1: Math.min(R.y + R.h, R.y + Math.ceil((p.oy + p.h) / HT) + 2) };
}

let sigHead: number | undefined;
/** how far the signal lamps are lifted above the pole foot (the renderer puts their glows there) */
export const hdSignalHead = () => sigHead ??= signalPost().head;

function* paintScene(m: GameMap, S: HdScene, g: G, includeEdits: boolean): Generator<void, HdSceneArt> {
  const lights: HdLight[] = [], props: HdProp[] = [];
  setSanctuaryOrigin(R.x, R.y); // the sanctuary's forest canopy is laid on a world grid
  /** record a light given in scene hd px; the scene's `light` scales the baked ones (live neon keeps its strength) */
  const dim = S.light ?? 1;
  const L = (x: number, y: number, w: number, h: number, color: string, a: number, refl = false, live = false) =>
    lights.push({ x: R.x * TILE + x / 2, y: R.y * TILE + y / 2, w: w / 2, h: h / 2, color, alpha: live ? a : a * dim, refl, live });
  const t = (tx: number, ty: number) => tileAt(m, tx, ty);
  const X = (tx: number) => (tx - R.x) * HT, Y = (ty: number) => (ty - R.y) * HT;

  // ---------------------------------------------------------------- ground
  const market = WORLD.plazas.find(p => p.name === 'Yatai Night Market'), lot = WORLD.plazas.find(p => p.name === 'Ghost Lot');
  const shrine = WORLD.plazas.find(p => p.name === 'Hanabi Shrine grounds');
  for (let ty = C.y0; ty < C.y1; ty++) for (let tx = C.x0; tx < C.x1; tx++) {
    const k = t(tx, ty), x = X(tx), y = Y(ty);
    if (tx === C.x0) yield;
    if (k === T.ROAD || lot && inRect(tx, ty, lot) && k === T.PLAZA) asphalt(g, x, y);
    else if (k === T.SIDEWALK || k === T.THIN && ty === 55) pavers(g, x, y, K.walk, K.walkHi, K.walkLo, 16);
    else if (k === T.ALLEY || k === T.BOLLARD) alleyFloor(g, x, y);
    else if (k === T.PLAZA || k === T.LOW || k === T.THIN || k === T.STALL) {
      if (market && inRect(tx, ty, market)) pavers(g, x, y, K.plazaWarm, mix(K.plazaWarm, K.warmDim, 0.25), dk(K.plazaWarm, 0.25), 16, 8);
      else if (shrine && inRect(tx, ty, shrine)) gravel(g, x, y);
      else pavers(g, x, y, K.plaza, K.plazaHi, K.walkLo, 32, 16);
    }
  }
  if (lot) parkingLot(g, X(lot.x), Y(lot.y), lot.w * HT, lot.h * HT);
  yield* roadMarkings(g, m, X, Y, t);
  yield* curbs(g, X, Y, t);
  railCrossings(g, m, X, Y, t, L);
  concourses(g, X, Y);
  const hp = WORLD.helipad, ramp = WORLD.plazas.find(p => p.name === 'Helipad ramp'), onPad = (l: Rect) => l.x >= hp.x - 1 && l.y >= hp.y - 1 && l.x < hp.x + hp.w + 1 && l.y < hp.y + hp.h + 1;
  if (ramp && inRect(hp.x, hp.y, R)) helipadPad(g, X(hp.x), Y(hp.y), hp.w * HT, hp.h * HT, X(ramp.x), ramp.w * HT, L);
  paintDeck(g, R, X, Y);
  // contact shadow where a wall meets the ground
  for (let ty = C.y0; ty < C.y1; ty++) for (let tx = C.x0; tx < C.x1; tx++) {
    if (!walkable(t(tx, ty)) || t(tx, ty - 1) !== T.BUILDING) continue;
    px(g, X(tx), Y(ty), HT, 3, alpha(K.night, 0.55)); px(g, X(tx), Y(ty) + 3, HT, 4, alpha(K.night, 0.25));
  }

  // ---------------------------------------------------------------- buildings (back to front so cast shadows land on lower roofs)
  const B = S.buildings;
  for (const b of B) {
    // a building away from this piece still reports the lights whose glow or reflection reaches it (painted into a dummy context)
    const d = distTo(b);
    yield;
    if (d.dx <= 3 && d.dy <= 3) yield* paintBuilding(g, b, X(b.x), Y(b.y), L);
    else if (d.dx <= LIGHT_REACH && d.dyUp <= LIGHT_REACH && d.dyDown <= LIGHT_REACH + 8) yield* paintBuilding(NULL_G, b, X(b.x), Y(b.y), L);
  }
  for (const a of B) for (const b of B) {
    // a taller building throws a shadow onto the roof of a lower neighbour on its right (light from the top-left)
    if (b.x !== a.x + a.w || b.face >= a.face) continue;
    const y0 = Math.max(a.y, b.y), y1 = Math.min(a.y + a.h - a.face, b.y + b.h - b.face); if (y1 <= y0) continue;
    const w = Math.min(b.w * HT, (a.face - b.face) * 10);
    px(g, X(b.x), Y(y0), w, (y1 - y0) * HT, alpha(K.night, 0.32)); dither(g, X(b.x) + w, Y(y0), 4, (y1 - y0) * HT, alpha(K.night, 0.3));
  }

  // ---------------------------------------------------------------- plaza furniture on collision tiles (ground layer)
  yield;
  for (const s of WORLD.stalls) if (inRect(s.x, s.y, R)) stall(g, X(s.x), Y(s.y), s.w * HT, s.h * HT, hash2(s.x, s.y), L);
  const artAt = (l: { x: number; y: number }) => CITY_ART.lowArt.find(a => a.x === l.x && a.y === l.y);
  for (const l of WORLD.lows) if (inRect(l.x, l.y, R) && !isClubTile(l.x, l.y) && !inSanctuary(l.x)) {
    const a = artAt(l);
    if (a?.kind === 'toro') toro(g, X(l.x), Y(l.y), l.w * HT, l.h * HT, L);
    else if (a?.kind === 'metro') metro(g, X(l.x), Y(l.y), l.w * HT, l.h * HT, L);
    else if (isDeckLow(l)) deckFence(g, X(l.x), Y(l.y), l.w * HT, l.h * HT);
    else if (onPad(l)) helipadFence(g, X(l.x), Y(l.y), l.w * HT, l.h * HT);
    else if (market && inRect(l.x, l.y, market)) crates(g, X(l.x), Y(l.y), l.w * HT);
    else planter(g, X(l.x), Y(l.y), l.w * HT, l.h * HT);
  }
  if (inRect(WORLD.club.block.x, WORLD.club.block.y, R)) paintClub(g, R, t, L); // Kumadori Club (hd-place-club.ts)
  paintExhaust(g, R, L); // Exhaust Shrine courtyard (hd-place-exhaust.ts)
  paintPaper9(g, R, L); // Paper Lantern 9 yard (hd-place-paper9.ts)
  paintKillSwitch(g, R, L); // Kill-Switch junkyard bar (hd-place-killswitch.ts)
  paintSanctuary(g, m, S, R, L); // Kanemori Sanctuary strip: road, courtyards, slope edges, mist (hd-sanctuary-place.ts)
  for (const r of WORLD.thins) if (inRect(r.x, r.y, R) && !inSanctuary(r.x)) railing(g, X(r.x), Y(r.y), r.w * HT, r.h * HT);

  // ---------------------------------------------------------------- upright props
  const prop = (cv: HTMLCanvasElement, wx: number, wy: number, ax: number, ay: number) => props.push({ canvas: cv, x: wx, y: wy, ax, ay });
  const lampLight = (lx: number, ly: number, f: 'n' | 's' | 'e' | 'w', own: boolean) => {
    const { cv, ax, ay, head } = lampPost(f);
    const wx = lx * TILE, wy = ly * TILE; if (own) prop(cv, wx, wy, ax, ay);
    const hx = wx + (head[0] - ax) / 2, hy = wy + (head[1] - ay) / 2;
    lights.push({ x: hx - 5, y: hy - 3, w: 10, h: 6, color: K.amber, alpha: 0.95 });
    // the pool lands on the ground under the head, nudged toward the road
    const toward = f === 's' ? [0, 10] : f === 'n' ? [0, -4] : f === 'e' ? [10, 0] : [-10, 0];
    lights.push({ x: wx + toward[0] - 32, y: wy + toward[1] - 13, w: 64, h: 26, color: K.amber, alpha: 0.42, pool: true });
  };
  for (const [lx, ly, f] of S.lamps) lampLight(lx, ly, f, true);
  // a neighbouring scene's lamp near the edge lights this side too, so its pool isn't cut off at the scene border
  for (const O of HD_SCENES) if (O !== S) for (const [lx, ly, f] of O.lamps) {
    if (lx > R.x - 4 && lx < R.x + R.w + 4 && ly > R.y - 4 && ly < R.y + R.h + 4) lampLight(lx, ly, f, false);
  }
  yield;
  const sig = signalPost();
  for (const it of m.intersections) {
    if (!inRect(Math.floor(it.cx / TILE), Math.floor(it.cy / TILE), R)) continue;
    for (const [dx, dy] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) prop(sig.cv, it.cx + dx * ((it.x1 - it.x0) / 2 + 4), it.cy + dy * ((it.y1 - it.y0) / 2 + 4), sig.ax, sig.ay);
  }
  const b = bollard();
  for (let ty = R.y; ty < R.y + R.h; ty++) for (let tx = R.x; tx < R.x + R.w; tx++) if (t(tx, ty) === T.BOLLARD && !inSanctuary(tx)) { // (the sanctuary's bollard ground is forest floor)
    prop(b.cv, tx * TILE + 4, ty * TILE + 11, b.ax, b.ay); prop(b.cv, tx * TILE + 12, ty * TILE + 11, b.ax, b.ay);
  }
  // trees stand in the Kanemori planters (low walls), so nobody walks through a trunk
  for (const l of WORLD.lows) if (inRect(l.x, l.y, R) && !isClubTile(l.x, l.y) && !inSanctuary(l.x) && !(market && inRect(l.x, l.y, market)) && !onPad(l) && !isDeckLow(l) && artAt(l)?.kind !== 'toro' && artAt(l)?.kind !== 'metro') for (let i = 1; i < l.w; i += 3) {
    const tr = tree(hash2(l.x + i, l.y)); prop(tr.cv, (l.x + i) * TILE + 8, l.y * TILE + 13, tr.ax, tr.ay);
  }
  if (inRect(hp.x, hp.y, R)) {
    const ws = windsock(), bc = beacon();
    prop(ws.cv, (hp.x + hp.w - 1.5) * TILE, (hp.y + 1.3) * TILE, ws.ax, ws.ay); prop(bc.cv, (hp.x + 1.5) * TILE, (hp.y + 1.3) * TILE, bc.ax, bc.ay);
    lights.push({ x: (hp.x + 1.5) * TILE - 4, y: (hp.y + 1.3) * TILE - 42, w: 8, h: 8, color: K.pink, alpha: 0.9, live: true });
  }
  // West Exit Koban: a free-standing police box on its solid footprint (map.ts kobanFootprint), door onto the doorstep
  for (const p of WORLD.pois) if (p.kind === 'koban' && inRect(Math.floor(p.x), Math.floor(p.y), R)) {
    const { x: wx, y: wy } = boothFeet(boothFootprint(p)!), hx = (wx - R.x * TILE) * 2, hy = (wy - R.y * TILE) * 2;
    kobanPad(g, hx, hy);
    const k = kobanBooth(); prop(k.cv, wx, wy, k.ax, k.ay);
    const c = cone();
    prop(c.cv, wx - 26, wy + 8, c.ax, c.ay); prop(c.cv, wx + 26, wy + 8, c.ax, c.ay);
    L(hx - 10, hy - 117, 20, 18, K.red, 0.8, false, true); // the red lamp
    L(hx + 45, hy - 130, 36, 40, K.cyan, 0.4); // holo badge glow
    L(hx - 30, hy - 57, 60, 36, K.glass, 0.35, true); // lit door
  }
  // Tower Lobby Hack terminal (position and zone read from the challenge data): a Kanemori data pillar plus a ground ring
  const tw = CHALLENGES.tower.rules;
  const twx = tw ? tw.tx * TILE + TILE / 2 : 0, twy = tw ? tw.ty * TILE + TILE / 2 : 0;
  if (tw && inRect(tw.tx, tw.ty, R)) {
    const tp = towerTerminal(); prop(tp.cv, twx, twy + 5, tp.ax, tp.ay);
    lights.push({ x: twx - 8, y: twy - 14, w: 16, h: 10, color: K.cyan, alpha: 0.7, pool: true });
  }
  placeArt(R, prop, lights);
  exhaustProps(R, prop, lights);
  paper9Props(R, prop, lights);
  killSwitchProps(R, prop, lights);
  sanctuaryProps(m, S, R, prop, lights);
  deckProps(R, prop, lights);
  // World Studio props: flat ones (manhole, drain) are painted into this ground image, before the baked light, so
  // players walk over them; upright ones stand and sort by their feet like the furniture above.
  const edits = includeEdits ? loadWorldEdits(m) : null;
  if (edits) for (const p of edits.props) if (inRect(Math.floor(p.x), Math.floor(p.y), R)) {
    const s = worldPropSprite(p, edits.assets);
    if (s.flat) g.drawImage(s.canvas, Math.round(X(p.x) - s.ax), Math.round(Y(p.y) - s.ay));
    else prop(s.canvas, p.x * TILE, p.y * TILE, s.ax, s.ay);
    yield;
  }
  yield* bakeLights(g, m, lights);
  gameplayMarks(g);
  if (tw && inRect(tw.tx, tw.ty, R)) {
    // the ring is a ground decal: clip it to walkable tiles so it never lands on the tower roofs
    g.save(); g.beginPath();
    const rt = Math.ceil(tw.zoneRadius / TILE) + 1;
    for (let ty = tw.ty - rt; ty <= tw.ty + rt; ty++) for (let tx = tw.tx - rt; tx <= tw.tx + rt; tx++) if (walkable(t(tx, ty))) g.rect(X(tx), Y(ty), HT, HT);
    g.clip();
    towerZone(g, (twx - R.x * TILE) * 2, (twy - R.y * TILE) * 2 + 4, tw.zoneRadius * 2);
    g.restore();
  }
  paintGunsmith(g, R);
  return { id: S.id, x: R.x * TILE, y: R.y * TILE, w: R.w * TILE, h: R.h * TILE, lights: lights.filter(l => l.live), all: lights, props };
}

/** how far (tiles) a baked light can reach from the building that reports it */
const LIGHT_REACH = 10;
/** gaps in tiles between a building and the piece being painted (0 when they overlap; dyUp/dyDown by which side the building is on) */
function distTo(b: { x: number; y: number; w: number; h: number }) {
  const dx = Math.max(C.x0 + 2 - (b.x + b.w), b.x - (C.x1 - 2), 0);
  const above = Math.max(C.y0 + 2 - (b.y + b.h), 0), below = Math.max(b.y - (C.y1 - 2), 0);
  return { dx, dy: above + below, dyUp: below, dyDown: above };
}
/** a 2D context that draws nothing (lights are still reported by the painters) */
const NULL_G: G = (() => { const f: any = new Proxy(function () { /* noop */ }, { get: (_t, k) => k === 'canvas' ? undefined : f, set: () => true, apply: () => f }); return f; })();

// ================================================================== baked light
/** Static light is painted into the scene image once (additive), so the renderer draws one ground texture instead of
 *  ~150 overlapping glow sprites every frame: soft glows, lamp pools, and a wet reflection below every lit storefront
 *  or sign, running down over walkable tiles only. Live (flickering) signs keep their glow sprite but bake their reflection. */
/** The gameplay marks the 1x art paints (city-art.ts): each shop/kiosk's dashed reach square and the clinic's recovery
 *  pad. Painted after the lights so they stay crisp; a scene covers the 1x ground, so it must repeat them. */
function gameplayMarks(g: G) {
  const hx = (wx: number) => (wx - R.x * TILE) * 2, hy = (wy: number) => (wy - R.y * TILE) * 2;
  g.save(); g.lineWidth = 2; g.setLineDash([6, 4]);
  for (const p of WORLD.pois) {
    if (!inRect(Math.floor(p.x), Math.floor(p.y), R)) continue;
    g.strokeStyle = poiColor(p.kind); g.strokeRect(hx(p.x * TILE) - 29, hy(p.y * TILE) - 29, 58, 58);
  }
  const rk = WORLD.pois.find(p => p.id === 'kiosk7'); if (rk && inRect(rk.x, rk.y, R)) paintRobotKioskDecal(g, hx(rk.x * TILE), hy(rk.y * TILE));
  g.restore();
  placeGround(g, R);
  paintHachiGround(g, R);
  paintNeighborhoodGround(g, R);
  paintKabukiEastGround(g, R);
  for (const s of WORLD.safehouses) if (s.kind === 'hotel' && inRect(s.x, s.y, R)) paintHotelMark(g, hx(s.x * TILE), hy(s.y * TILE), s.w * HT, s.h * HT);
  for (const p of WORLD.pois) if (p.id === 'broker' && inRect(Math.floor(p.x), Math.floor(p.y), R)) paintBroker(g, hx(p.x * TILE), hy(p.y * TILE));
  for (const z of WORLD.healZones) {
    if (!inRect(z.x, z.y, R)) continue;
    const x = hx(z.x * TILE), y = hy(z.y * TILE), w = z.w * HT, h = z.h * HT;
    if (z.circle) { // a round zone (the onsen): a soft disc with a ring, no clinic cross
      const c = z.circle, cx = hx(c.cx * TILE), cy = hy(c.cy * TILE), rr = c.r * HT;
      g.save(); g.fillStyle = alpha(K.cyan, 0.14); g.strokeStyle = alpha(K.cyan, 0.85); g.lineWidth = 2;
      g.beginPath(); g.arc(cx, cy, rr, 0, Math.PI * 2); g.fill(); g.stroke();
      g.strokeStyle = alpha(K.cyan, 0.4); g.setLineDash([6, 6]); g.beginPath(); g.arc(cx, cy, rr - 5, 0, Math.PI * 2); g.stroke(); g.restore();
      continue;
    }
    px(g, x, y, w, h, alpha(K.cyan, 0.2)); frame(g, x + 2, y + 2, w - 4, h - 4, K.cyan); frame(g, x + 4, y + 4, w - 8, h - 8, alpha(K.cyan, 0.4));
    px(g, x + w / 2 - 4, y + h / 2 - 14, 8, 28, K.white); px(g, x + w / 2 - 14, y + h / 2 - 4, 28, 8, K.white);
  }
  for (const s of WORLD.safehouses) if (s.kind === 'den' && inRect(s.x, s.y, R)) denMarks(g, hx(s.x * TILE), hy(s.y * TILE), s.w * HT, s.h * HT);
}

/** The Underground Karaoke Den: a stairwell down at the west end of its zone, a neon pink pool and dashed outline over
 *  the zone, a stencilled カラオケ and arrow on the ground, a mic icon and a lit door lamp. */
function denMarks(g: G, x: number, y: number, w: number, h: number) {
  const pink = K.pink;
  g.save(); // light pool over the zone
  const cx = x + w / 2, cy = y + h / 2; g.translate(cx, cy); g.scale(1, h / w * 1.3);
  const grd = g.createRadialGradient(0, 0, 0, 0, 0, w * 0.62);
  grd.addColorStop(0, alpha(pink, 0.34)); grd.addColorStop(0.7, alpha(pink, 0.14)); grd.addColorStop(1, alpha(pink, 0));
  g.fillStyle = grd; g.fillRect(-w, -w, w * 2, w * 2); g.restore();
  g.save(); g.lineWidth = 2; g.setLineDash([8, 5]); g.strokeStyle = alpha(pink, 0.8); g.strokeRect(x + 3, y + 3, w - 6, h - 6); g.restore();
  // stairwell: steps going down into a dark mouth, cyan-lit handrail
  const sx = x + 8, sy = y + 8, sw = 36, sh = h - 16;
  px(g, sx - 2, sy - 2, sw + 4, sh + 4, K.ink); px(g, sx, sy, sw, sh, K.night);
  for (let i = 0; i < 6; i++) { const yy = sy + 2 + i * 8; px(g, sx + 2, yy, sw - 4, 6, mix(K.concrete, K.ink, 0.2 + i * 0.12)); px(g, sx + 2, yy, sw - 4, 1, mix(K.concreteHi, K.ink, i * 0.14)); }
  px(g, sx, sy, 2, sh, K.cyan); px(g, sx + sw - 2, sy, 2, sh, K.cyan);
  px(g, sx + 2, sy + sh - 6, sw - 4, 6, alpha(pink, 0.5));
  // stencilled sign and arrow on the paving
  const tx = sx + sw + 8, tw = textW('カラオケ', 2);
  text(g, 'カラオケ', tx + 1, y + 11, K.ink, 2); text(g, 'カラオケ', tx, y + 10, lt(pink, 0.55), 2);
  const ay = y + h - 18; px(g, tx, ay - 2, 32, 4, alpha(K.white, 0.7)); px(g, tx, ay - 7, 4, 14, alpha(K.white, 0.7)); px(g, tx + 4, ay - 4, 4, 8, alpha(K.white, 0.7)); px(g, tx + 8, ay - 1, 1, 2, alpha(K.white, 0.7));
  // mic icon: capsule head on a handle
  const mx = tx + tw - 14, my = ay - 8;
  disc(g, mx + 6, my + 4, 5, 5, K.ink); disc(g, mx + 6, my + 4, 4, 4, lt(pink, 0.4)); px(g, mx + 5, my + 8, 3, 12, K.ink); px(g, mx + 6, my + 9, 1, 10, K.metalHi);
  // door lamp over the stairs
  disc(g, sx + sw / 2, sy - 6, 3, 3, K.ink); disc(g, sx + sw / 2, sy - 6, 2, 2, K.white);
  g.save(); g.globalCompositeOperation = 'lighter'; g.translate(sx + sw / 2, sy - 4); g.scale(1, 0.6);
  const lg = g.createRadialGradient(0, 0, 0, 0, 0, 40); lg.addColorStop(0, alpha(pink, 0.6)); lg.addColorStop(1, alpha(pink, 0));
  g.fillStyle = lg; g.fillRect(-40, -40, 80, 80); g.restore();
}

function* bakeLights(g: G, m: GameMap, lights: HdLight[]) {
  const hx = (wx: number) => (wx - R.x * TILE) * 2, hy = (wy: number) => (wy - R.y * TILE) * 2;
  g.save(); g.globalCompositeOperation = 'lighter';
  let cnt = 0;
  for (const l of lights) {
    if (++cnt % 16 === 0) yield;
    const cx = hx(l.x + l.w / 2), cy = hy(l.y + l.h / 2);
    if (!l.live) {
      const rx = l.pool ? l.w * 1.3 : l.w + 34, ry = l.pool ? l.h * 1.3 : l.h + 28; // world px, a little wider than the old glow sprites
      const rxh = rx * 2, ryh = ry * 2; // hd px
      if (!PB || cx + rxh > PB.x0 && cx - rxh < PB.x1 && cy + ryh > PB.y0 && cy - ryh < PB.y1) {
      g.save(); g.translate(cx, cy); g.scale(1, ry / rx);
      const grd = g.createRadialGradient(0, 0, 0, 0, 0, rx);
      grd.addColorStop(0, alpha(l.color, Math.min(1, 1.2 * l.alpha))); grd.addColorStop(0.3, alpha(l.color, 0.5 * l.alpha)); grd.addColorStop(1, alpha(l.color, 0));
      g.fillStyle = grd; g.fillRect(-rx, -rx, rx * 2, rx * 2); g.restore();
      }
    }
    if (!l.refl || l.alpha < 0.3) continue;
    const tx = Math.floor((l.x + l.w / 2) / TILE); let ty = Math.floor((l.y + l.h) / TILE);
    while (ty < m.h && tileAt(m, tx, ty) === T.BUILDING) ty++;
    let n = 0; while (n < 12 && walkable(tileAt(m, tx, ty + n))) n++;
    if (!n) continue;
    // broken horizontal ripples, fading downward over the sidewalk and out across the wet road
    const w = Math.max(10, Math.min(110, l.w * 2.1)), h = Math.min(n * HT - 4, (40 + l.alpha * 80) * 2), y0 = hy(ty * TILE) + 2, a0 = Math.min(0.38, l.alpha * 0.6);
    if (PB && (y0 > PB.y1 || y0 + h < PB.y0 || cx + w < PB.x0 - 10 || cx - w > PB.x1 + 10)) continue;
    for (let j = 0; j < h; j++) {
      if (j % 3 === 2) continue;
      const k = j / h, rw = Math.round(w * (1 - hash2(j, tx) * 0.5)), off = Math.round((hash2(tx, j) - 0.5) * 10);
      px(g, Math.round(cx - rw / 2) + off, y0 + j, rw, 1, alpha(l.color, a0 * Math.pow(1 - k, 1.6)));
    }
  }
  g.restore();
}

// ================================================================== ground materials
function asphalt(g: G, x: number, y: number) {
  px(g, x, y, HT, HT, K.asphalt);
  speckle(g, x, y, HT, HT, K.asphaltHi, 0.06, 1);
  speckle(g, x, y, HT, HT, K.asphaltLo, 0.08, 2);
  speckle(g, x, y, HT, HT, K.asphaltWet, 0.03, 3, 2);
}
function pavers(g: G, x: number, y: number, base: string, hi: string, lo: string, sw: number, sh = sw) {
  px(g, x, y, HT, HT, base);
  for (let j = 0; j < HT; j += sh) for (let i = 0; i < HT; i += sw) {
    const v = hash2(x + i, y + j);
    if (v < 0.22) px(g, x + i, y + j, sw, sh, hi); else if (v > 0.84) px(g, x + i, y + j, sw, sh, lo);
    px(g, x + i, y + j, sw, 1, K.seam); px(g, x + i, y + j, 1, sh, K.seam); // seams
    px(g, x + i + 1, y + j + 1, sw - 2, 1, alpha(K.white, 0.05)); // lit top edge
  }
  speckle(g, x, y, HT, HT, lo, 0.03, 5);
}
function alleyFloor(g: G, x: number, y: number) {
  px(g, x, y, HT, HT, K.alley);
  speckle(g, x, y, HT, HT, K.alleyLo, 0.12, 6);
  speckle(g, x, y, HT, HT, K.walkLo, 0.04, 7, 2);
  if (hash2(x, y + 9) < 0.18) disc(g, x + 10 + Math.floor(hash2(x, y) * 12), y + 18, 7, 3, K.asphaltWet); // standing water
}
function parkingLot(g: G, x: number, y: number, w: number, h: number) {
  for (let i = 12; i < w - 8; i += 44) { px(g, x + i, y + 10, 2, 54, K.paintLo); px(g, x + i, y + h - 64, 2, 54, K.paintLo); }
  for (let i = 18; i < w - 30; i += 44) { px(g, x + i + 10, y + 12, 18, 3, K.concreteLo); px(g, x + i + 10, y + h - 15, 18, 3, K.concreteLo); } // wheel stops
  speckle(g, x, y, w, h, dk(K.asphalt, 0.3), 0.02, 9, 3); // oil
}
function* roadMarkings(g: G, m: GameMap, X: (t: number) => number, Y: (t: number) => number, t: (x: number, y: number) => T) {
  const RW = m.roadW;
  for (const hy of m.hRoads) {
    yield;
    if (hy < R.y || hy >= R.y + R.h) continue;
    for (let tx = C.x0; tx < C.x1; tx++) {
      const crossing = t(tx, hy - 1) === T.ROAD || t(tx, hy + RW) === T.ROAD; // the vertical road passes through here
      const walk = !crossing && (t(tx - 1, hy - 1) === T.ROAD || t(tx - 2, hy - 1) === T.ROAD || t(tx + 1, hy - 1) === T.ROAD || t(tx + 2, hy - 1) === T.ROAD);
      const x = X(tx);
      if (crossing || walk) continue;
      // centre: double amber line broken into dashes, faint edge lines
      for (let i = 0; i < HT; i++) if (((x + i) % 48) < 26) { px(g, x + i, Y(hy) + RW * HT / 2 - 3, 1, 2, K.lane); px(g, x + i, Y(hy) + RW * HT / 2 + 1, 1, 2, K.lane); }
      px(g, x, Y(hy) + 7, HT, 1, K.paintLo); px(g, x, Y(hy) + RW * HT - 8, HT, 1, K.paintLo);
    }
  }
  for (const vx of m.vRoads) {
    yield;
    if (vx < R.x || vx >= R.x + R.w) continue;
    for (let ty = C.y0; ty < C.y1; ty++) {
      const crossing = t(vx - 1, ty) === T.ROAD || t(vx + RW, ty) === T.ROAD;
      const walk = !crossing && (t(vx - 1, ty - 1) === T.ROAD || t(vx - 1, ty - 2) === T.ROAD || t(vx - 1, ty + 1) === T.ROAD || t(vx - 1, ty + 2) === T.ROAD);
      const y = Y(ty);
      if (crossing || walk || t(vx, ty) !== T.ROAD) continue;
      for (let j = 0; j < HT; j++) if (((y + j) % 48) < 26) { px(g, X(vx) + RW * HT / 2 - 3, y + j, 2, 1, K.lane); px(g, X(vx) + RW * HT / 2 + 1, y + j, 2, 1, K.lane); }
      px(g, X(vx) + 7, y, 1, HT, K.paintLo); px(g, X(vx) + RW * HT - 8, y, 1, HT, K.paintLo);
    }
  }
  // zebra crossings on the four arms of each intersection (bars run with the traffic), with stop lines
  const bar = (x: number, y: number, w: number, h: number) => { px(g, x, y, w, h, K.paint); speckle(g, x, y, w, h, K.paintLo, 0.16, 11); px(g, x, y + h - 1, w, 1, K.paintLo); };
  for (const it of m.intersections) {
    yield;
    const x0 = X(it.x0 / TILE), y0 = Y(it.y0 / TILE), x1 = X(it.x1 / TILE), y1 = Y(it.y1 / TILE), S = 2 * HT;
    if (it.x0 / TILE < R.x || it.x1 / TILE > R.x + R.w || it.y0 / TILE < R.y || it.y1 / TILE > R.y + R.h) continue;
    for (let j = y0 + 6; j + 9 <= y1 - 2; j += 15) { bar(x0 - S + 6, j, S - 12, 9); bar(x1 + 6, j, S - 12, 9); }
    for (let i = x0 + 6; i + 9 <= x1 - 2; i += 15) { bar(i, y0 - S + 6, 9, S - 12); bar(i, y1 + 6, 9, S - 12); }
    // stop lines on the inbound half of each arm (traffic keeps left)
    px(g, x0 - S - 6, y0 + 4, 4, (y1 - y0) / 2 - 6, K.paint); px(g, x1 + S + 2, y0 + (y1 - y0) / 2 + 2, 4, (y1 - y0) / 2 - 6, K.paint);
    px(g, x0 + (x1 - x0) / 2 + 2, y0 - S - 6, (x1 - x0) / 2 - 6, 4, K.paint); px(g, x0 + 4, y1 + S + 2, (x1 - x0) / 2 - 6, 4, K.paint);
  }
  // manholes and patched asphalt at fixed, seeded spots on plain road
  for (let ty = C.y0; ty < C.y1; ty++) for (let tx = C.x0; tx < C.x1; tx++) {
    if (tx === C.x0) yield;
    if (t(tx, ty) !== T.ROAD) continue;
    const v = hash2(tx * 3, ty * 5);
    if (v < 0.025) { const cx = X(tx) + 16, cy = Y(ty) + 16; disc(g, cx, cy, 9, 9, K.asphaltLo); disc(g, cx, cy, 7, 7, K.metalLo); for (let k = -5; k <= 5; k += 3) px(g, cx - 6, cy + k, 13, 1, K.asphaltLo); px(g, cx - 7, cy - 8, 6, 1, K.metal); }
    else if (v > 0.965) { px(g, X(tx) + 4, Y(ty) + 6, 22, 14, K.asphaltHi); speckle(g, X(tx) + 4, Y(ty) + 6, 22, 14, K.asphalt, 0.3, 13); }
  }
}
function* curbs(g: G, X: (t: number) => number, Y: (t: number) => number, t: (x: number, y: number) => T) {
  for (let ty = C.y0; ty < C.y1; ty++) for (let tx = C.x0; tx < C.x1; tx++) {
    if (tx === C.x0) yield;
    const k = t(tx, ty); if (k !== T.SIDEWALK) continue;
    const x = X(tx), y = Y(ty);
    if (t(tx, ty + 1) === T.ROAD) { // the camera sees this curb's face
      px(g, x, y + HT - 4, HT, 3, K.curb); px(g, x, y + HT - 1, HT, 1, K.curbLo);
      px(g, x, y + HT, HT, 3, K.curbFace); px(g, x, y + HT + 3, HT, 1, K.curbLo);
      if (hash2(tx, ty * 7) < 0.12) { px(g, x + 8, y + HT + 4, 16, 4, K.ink); for (let i = 9; i < 24; i += 3) px(g, x + i, y + HT + 5, 1, 2, K.metalLo); } // drain grate
    }
    if (t(tx, ty - 1) === T.ROAD) { px(g, x, y, HT, 1, K.curbLo); px(g, x, y + 1, HT, 3, K.curb); px(g, x, y + 4, HT, 1, K.walkLo); }
    if (t(tx + 1, ty) === T.ROAD) { px(g, x + HT - 4, y, 3, HT, K.curb); px(g, x + HT - 1, y, 1, HT, K.curbLo); px(g, x + HT, y, 2, HT, K.curbFace); }
    if (t(tx - 1, ty) === T.ROAD) { px(g, x, y, 1, HT, K.curbLo); px(g, x + 1, y, 3, HT, K.curb); }
    // tactile paving where a crosswalk lands
    const atCross = (t(tx, ty + 1) === T.ROAD && t(tx - 2, ty + 1) !== T.ROAD) || (t(tx, ty - 1) === T.ROAD && t(tx - 2, ty - 1) !== T.ROAD) ||
      (t(tx + 1, ty) === T.ROAD && t(tx + 1, ty - 2) !== T.ROAD) || (t(tx - 1, ty) === T.ROAD && t(tx - 1, ty - 2) !== T.ROAD);
    if (atCross) {
      const bx = x + 6, by = y + 6; px(g, bx, by, 20, 20, K.tactile);
      for (let j = 2; j < 20; j += 4) for (let i = 2; i < 20; i += 4) px(g, bx + i, by + j, 2, 2, K.tactileLo);
    }
  }
}


// ================================================================== plaza furniture
function stall(g: G, x: number, y: number, w: number, h: number, v: number, L: LightFn) {
  const a = v < 0.5 ? K.red : K.amber, b = v < 0.5 ? K.white : K.redLo;
  shadow(g, x + 4, y + h - 6, w, 8, 0.4);
  // canopy seen from above, then the lit counter facing the walkway
  awning(g, x + 2, y + 2, w - 4, h - 30, a, b);
  px(g, x + 4, y + h - 26, w - 8, 22, K.ink); bands(g, x + 5, y + h - 25, w - 10, 12, K.warmHi, K.warm, 3); px(g, x + 5, y + h - 13, w - 10, 8, K.wood); px(g, x + 5, y + h - 13, w - 10, 1, K.woodHi);
  for (let i = 10; i < w - 10; i += 14) { px(g, x + i, y + h - 22, 6, 4, K.redLo); px(g, x + i + 1, y + h - 23, 4, 1, K.white); } // bowls
  for (let i = 8; i < w - 8; i += 20) lantern(g, x + i, y + h - 44, i % 40 ? K.red : K.amber, L);
  L(x + 5, y + h - 25, w - 10, 12, K.amber, 0.45, true);
}
function crates(g: G, x: number, y: number, w: number) {
  for (let i = 0; i < w; i += 16) { shadow(g, x + i + 3, y + 10, 14, 20); px(g, x + i, y + 6, 14, 22, K.ink); px(g, x + i + 1, y + 7, 12, 8, K.woodHi); px(g, x + i + 1, y + 15, 12, 12, K.wood); px(g, x + i + 1, y + 20, 12, 1, K.woodLo); }
}
function planter(g: G, x: number, y: number, w: number, h: number) {
  px(g, x, y + 4, w, h - 4, K.ink); px(g, x + 1, y + 5, w - 2, 8, K.concreteHi); px(g, x + 1, y + 13, w - 2, h - 15, K.concrete); px(g, x + 1, y + h - 3, w - 2, 2, K.concreteLo);
  px(g, x + 3, y + 7, w - 6, 5, K.leafLo); for (let i = 4; i < w - 4; i += 5) disc(g, x + i, y + 6, 3, 2, i % 10 ? K.leaf : K.leafHi);
  shadow(g, x + 4, y + h, w - 4, 4, 0.3);
}
// ================================================================== landmarks the 1x art had (city-art.ts lowArt, platforms, levelCrossings)
/** raked shrine gravel */
function gravel(g: G, x: number, y: number) {
  const base = mix(K.walk, K.warmDim, 0.3);
  px(g, x, y, HT, HT, base);
  speckle(g, x, y, HT, HT, lt(base, 0.18), 0.12, 61); speckle(g, x, y, HT, HT, dk(base, 0.3), 0.1, 62);
  for (let j = 6; j < HT; j += 8) px(g, x, y + j, HT, 1, alpha(K.night, 0.12)); // rake lines
}
/** stone lantern: base, post, lit firebox, cap */
function toro(g: G, x: number, y: number, w: number, h: number, L: LightFn) {
  const st = K.metal, stHi = K.metalHi, cx = x + w / 2;
  shadow(g, cx - 10, y + h - 10, 24, 8);
  px(g, cx - 12, y + h - 14, 24, 10, K.ink); px(g, cx - 11, y + h - 13, 22, 7, st);
  px(g, cx - 4, y + 22, 8, h - 36, K.ink); px(g, cx - 3, y + 22, 6, h - 36, st);
  px(g, cx - 10, y + 10, 20, 16, K.ink); px(g, cx - 9, y + 11, 18, 14, stHi); px(g, cx - 5, y + 14, 10, 7, K.warmHi);
  px(g, cx - 14, y + 4, 28, 8, K.ink); px(g, cx - 13, y + 5, 26, 4, stHi); px(g, cx - 2, y, 4, 5, K.ink);
  L(cx - 5, y + 14, 10, 7, K.amber, 0.55, true);
}
/** metro entrance: stairs going down under a glass canopy, a blue M roundel */
function metro(g: G, x: number, y: number, w: number, h: number, L: LightFn) {
  shadow(g, x + 4, y + 6, w, h);
  px(g, x, y, w, h, K.ink); px(g, x + 4, y + 4, w - 8, h - 8, K.metalLo);
  for (let j = y + 10; j < y + h - 6; j += 6) { px(g, x + 8, j, w - 16, 2, K.metal); px(g, x + 8, j + 2, w - 16, 1, K.ink); }
  px(g, x + 2, y + 2, w - 4, 4, K.glassHi); px(g, x + 2, y + 2, 4, h - 4, K.glassHi); px(g, x + w - 6, y + 2, 4, h - 4, K.glassHi);
  const rx = x + w - 16, ry = y + 12;
  disc(g, rx, ry, 10, 10, K.ink); disc(g, rx, ry, 8, 8, dk(K.cyan, 0.3));
  px(g, rx - 4, ry - 4, 2, 9, K.white); px(g, rx + 3, ry - 4, 2, 9, K.white); px(g, rx - 2, ry - 3, 2, 2, K.white); px(g, rx + 1, ry - 3, 2, 2, K.white); px(g, rx, ry - 1, 1, 2, K.white);
  L(rx - 10, ry - 10, 20, 20, K.cyan, 0.5, true);
}
/** rails across the road where it meets the station line: warning stripes, and signal posts with red lamps */
function railCrossings(g: G, m: GameMap, X: (tx: number) => number, Y: (ty: number) => number, t: (tx: number, ty: number) => T, L: LightFn) {
  for (const r of WORLD.rails) {
    const top = Y(r.y), h = r.h * HT, tracks = [Math.round(h * 0.3), Math.round(h * 0.7)];
    for (let tx = Math.max(r.x, R.x); tx < Math.min(r.x + r.w, R.x + R.w); tx++) {
      const k = t(tx, r.y); if (k !== T.ROAD && k !== T.SIDEWALK) continue;
      const x = X(tx);
      for (const c of tracks) for (const o of [-12, 8]) { px(g, x, top + c + o - 2, HT, 8, K.ink); px(g, x, top + c + o, HT, 4, K.metal); px(g, x, top + c + o, HT, 1, K.metalHi); }
      if (k === T.ROAD) for (const y of [top + 4, top + h - 12]) for (let i = 0; i < HT; i += 8) px(g, x + i, y, 4, 8, (tx * 4 + i / 8) % 2 ? K.warm : K.ink);
      const roadW = t(tx - 1, r.y) === T.ROAD, roadE = t(tx + 1, r.y) === T.ROAD;
      if (k === T.SIDEWALK && (roadW || roadE)) for (const y of [top - 4, top + h - 16]) {
        const px0 = x + (roadW ? 6 : 14);
        px(g, px0, y, 12, 20, K.ink); px(g, px0 + 2, y + 2, 8, 16, K.white); px(g, px0 + 2, y + 6, 8, 4, K.red);
        L(px0 + 2, y + 6, 8, 4, K.red, 0.4);
      }
    }
  }
}
/** station concourses (plazas across the rail line): a tactile guide line down the middle, glass railings over the tracks */
function concourses(g: G, X: (tx: number) => number, Y: (ty: number) => number) {
  const over = (a: Rect, b: Rect) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
  for (const c of WORLD.plazas.filter(pl => WORLD.rails.some(r => over(r, pl)))) {
    if (!over(c, R)) continue;
    const cx = X(c.x) + c.w * HT / 2;
    for (let y = Y(c.y) + 36; y < Y(c.y + c.h) - 36; y += 8) { px(g, cx - 4, y, 8, 5, K.warm); px(g, cx - 4, y, 8, 1, K.warmHi); }
    for (const r of WORLD.rails) {
      const y0 = Math.max(c.y, r.y), y1 = Math.min(c.y + c.h, r.y + r.h); if (y1 <= y0) continue;
      railing(g, X(c.x) - 16, Y(y0), HT, (y1 - y0) * HT); railing(g, X(c.x + c.w) - 16, Y(y0), HT, (y1 - y0) * HT);
    }
  }
}

/** a glass barrier on steel posts: it blocks movement, so it has to read as solid (the 1x art drew teal panes) */
function railing(g: G, x: number, y: number, w: number, h: number) {
  const horiz = w >= h, bx = horiz ? x : x + 9, by = horiz ? y + 9 : y, bw = horiz ? w : 14, bh = horiz ? 14 : h;
  shadow(g, bx + 3, by + 4, bw, bh, 0.35);
  px(g, bx, by, bw, bh, K.ink); px(g, bx + 1, by + 1, bw - 2, bh - 2, alpha(K.cyan, 0.35));
  px(g, bx + 2, by + 2, horiz ? bw - 4 : 2, horiz ? 2 : bh - 4, alpha(K.white, 0.5)); // glass edge highlight
  for (let i = 0; i < (horiz ? bw : bh); i += 32) horiz ? px(g, bx + i, by - 1, 3, bh + 2, K.metalHi) : px(g, bx - 1, by + i, bw + 2, 3, K.metalHi); // posts
}

// ================================================================== upright props
const lampCache = new Map<string, ReturnType<typeof paintLamp>>();
/** one canvas per facing, shared by every lamp that faces that way */
function lampPost(f: string) { let l = lampCache.get(f); if (!l) lampCache.set(f, l = paintLamp(f)); return l; }
function paintLamp(f: string) {
  const [cv, g] = canvas(30, 84), ax = 15, ay = 82;
  px(g, ax - 3, ay - 3, 7, 3, K.ink); px(g, ax - 2, ay - 2, 5, 2, K.metalLo); // foot
  px(g, ax - 1, 14, 3, ay - 14, K.ink); px(g, ax, 14, 1, ay - 16, K.metalHi);
  let head: [number, number];
  if (f === 'e' || f === 'w') {
    const d = f === 'e' ? 1 : -1, hx = ax + d * 10;
    px(g, Math.min(ax, hx), 13, 11, 2, K.ink);
    px(g, hx - 4, 12, 9, 5, K.ink); px(g, hx - 3, 16, 7, 2, K.warmHi); head = [hx, 18];
  } else {
    px(g, ax - 5, 9, 11, 6, K.ink); px(g, ax - 4, 10, 9, 2, K.metal); px(g, ax - 4, 14, 9, 2, K.warmHi); head = [ax, 15];
  }
  return { cv, ax, ay, head };
}
function signalPost() {
  const [cv, g] = canvas(16, 70), ax = 8, ay = 68;
  px(g, ax - 3, ay - 2, 7, 2, K.ink); px(g, ax - 1, 20, 3, ay - 20, K.ink); px(g, ax, 20, 1, ay - 22, K.metal);
  px(g, ax - 5, 4, 11, 18, K.ink); px(g, ax - 4, 5, 9, 16, K.metalLo);
  for (const j of [7, 12, 17]) disc(g, ax, j, 2, 2, K.ink);
  px(g, ax - 6, 4, 13, 1, K.metal);
  return { cv, ax, ay, head: (ay - 12) / 2 };
}
/** the hack zone on the forecourt: a dashed ring, an inner ring, cable runs and a floor plate under the terminal (hd px) */
function towerZone(g: G, cx: number, cy: number, r: number) {
  g.save(); g.translate(cx, cy); g.scale(1, 0.62);
  g.fillStyle = alpha(K.cyan, 0.08); g.beginPath(); g.arc(0, 0, r, 0, Math.PI * 2); g.fill();
  g.lineWidth = 3; g.strokeStyle = alpha(K.cyan, 0.6); g.setLineDash([14, 10]); g.beginPath(); g.arc(0, 0, r, 0, Math.PI * 2); g.stroke();
  g.setLineDash([]); g.lineWidth = 2; g.strokeStyle = alpha(K.cyan, 0.3); g.beginPath(); g.arc(0, 0, r * 0.5, 0, Math.PI * 2); g.stroke();
  g.restore();
  for (const d of [-1, 1]) { // cable runs along the ground to the pillar
    px(g, cx + (d < 0 ? -r * 0.5 : 14), cy + 12, r * 0.5 - 14, 3, K.ink); px(g, cx + (d < 0 ? -r * 0.5 : 14), cy + 13, r * 0.5 - 14, 1, alpha(K.cyan, 0.5));
  }
  px(g, cx - 18, cy + 2, 36, 12, K.ink); frame(g, cx - 17, cy + 3, 34, 10, alpha(K.cyan, 0.6));
}
/** a Kanemori lobby data pillar: dark column, glowing screen with a K mark, status lights and a cable at the foot */
function towerTerminal() {
  const [cv, g] = canvas(36, 74), ax = 18, ay = 66;
  px(g, ax - 11, ay - 6, 22, 6, K.ink); px(g, ax - 10, ay - 5, 20, 2, K.concreteHi);
  px(g, ax - 8, 14, 16, ay - 18, K.ink); px(g, ax - 7, 15, 14, ay - 20, K.metalLo); px(g, ax - 7, 15, 2, ay - 20, K.metal);
  px(g, ax - 9, 8, 18, 8, K.ink); px(g, ax - 8, 9, 16, 3, K.metalHi); // cap
  px(g, ax - 6, 20, 12, 18, K.ink); px(g, ax - 5, 21, 10, 16, K.glassHi); px(g, ax - 5, 21, 10, 5, alpha(K.white, 0.35));
  px(g, ax - 1, 25, 2, 8, K.ink); px(g, ax + 1, 25, 3, 2, K.ink); px(g, ax + 1, 31, 3, 2, K.ink); px(g, ax - 3, 28, 3, 2, K.ink); // K mark
  px(g, ax - 6, 42, 12, 1, K.cyan); for (let i = 0; i < 3; i++) px(g, ax - 5 + i * 4, 46, 3, 2, i === 1 ? K.pink : K.cyan);
  px(g, ax - 6, 52, 12, 4, K.ink); px(g, ax - 5, 53, 10, 1, K.metalHi); // slot
  px(g, ax + 8, ay - 3, 8, 2, K.ink); px(g, ax + 14, ay - 3, 2, 1, K.cyan); // cable
  return { cv, ax, ay };
}
function bollard() {
  const [cv, g] = canvas(8, 20), ax = 4, ay = 19;
  px(g, 1, 4, 6, 15, K.ink); px(g, 2, 5, 4, 13, K.metal); px(g, 2, 5, 1, 13, K.metalHi); px(g, 2, 8, 4, 2, K.warm); px(g, 1, 3, 6, 2, K.metalHi);
  return { cv, ax, ay };
}
/** hazard-striped threshold in front of the koban door (ground decal, hd px centred on the booth's feet) */
function kobanPad(g: G, cx: number, cy: number) {
  const x = cx - 36, y = cy + 4;
  px(g, x - 1, y - 1, 74, 14, K.ink);
  for (let j = 0; j < 12; j++) for (let i = 0; i < 72; i += 8) px(g, x + i + (j >> 1) % 8, y + j, 4, 1, i % 16 ? K.paint : K.cyan);
}
/** Japanese police box with a 2089 twist: red lamp, 交番/KOBAN sign, glass door, a cyan holo badge beside it */
function kobanBooth() {
  const [cv, g] = canvas(96, 100), ax = 36, ay = 94, bx = 10, w = 52;
  shadow(g, bx + 4, ay - 4, w + 10, 8, 0.4);
  px(g, bx - 2, 84, w + 4, 8, K.ink); px(g, bx - 1, 85, w + 2, 6, K.concrete); px(g, bx - 1, 85, w + 2, 1, K.concreteHi);
  // walls
  px(g, bx, 48, w, 38, K.ink); px(g, bx + 1, 49, w - 2, 36, K.plaster); px(g, bx + 1, 49, 4, 36, K.plasterLo);
  speckle(g, bx + 1, 49, w - 2, 36, K.plasterLo, 0.05, 5);
  px(g, bx + 1, 70, w - 2, 3, K.cyan); px(g, bx + 1, 73, w - 2, 1, dk(K.cyan, 0.5)); // blue-white police stripe
  // door and windows
  px(g, bx + 18, 56, 18, 30, K.ink); px(g, bx + 19, 57, 16, 29, K.glass); px(g, bx + 19, 57, 3, 29, K.glassHi); px(g, bx + 26, 57, 1, 29, K.ink); px(g, bx + 34, 70, 1, 4, K.metalHi);
  for (const wx0 of [bx + 6, bx + 40]) { px(g, wx0, 58, 10, 12, K.ink); px(g, wx0 + 1, 59, 8, 10, K.glass); px(g, wx0 + 1, 59, 8, 2, K.glassHi); }
  // sign band
  px(g, bx, 44, w, 12, K.ink); px(g, bx + 1, 45, w - 2, 10, K.night);
  text(g, '交番', bx + 4, 48, K.warmHi, 1, 1);
  text(g, 'KOBAN', bx + 22, 48, K.cyan, 1, 1);
  // roof
  px(g, bx - 4, 38, w + 8, 7, K.ink); px(g, bx - 3, 39, w + 6, 5, K.metalLo); px(g, bx - 3, 39, w + 6, 1, K.metalHi);
  px(g, bx + 2, 32, w - 4, 7, K.ink); px(g, bx + 3, 33, w - 6, 5, K.metal); px(g, bx + 3, 33, w - 6, 1, K.metalHi);
  // red lamp
  const cx = bx + w / 2;
  px(g, cx - 2, 26, 5, 7, K.ink); px(g, cx - 1, 27, 3, 6, K.metalLo);
  disc(g, cx, 20, 9, 8, K.ink); disc(g, cx, 20, 8, 7, K.red); disc(g, cx - 2, 18, 5, 4, K.pink); disc(g, cx - 3, 16, 2, 2, K.warmHi);
  // holo badge: translucent cyan shield from a small projector on the roof edge
  px(g, bx + w + 2, 40, 4, 4, K.ink); px(g, bx + w + 3, 41, 2, 2, K.cyan);
  const sx = bx + w + 12;
  for (let j = 0; j < 26; j++) { const hw = j < 18 ? 8 : Math.max(0, 8 - (j - 18)); px(g, sx - hw, 8 + j, hw * 2 + 1, 1, j % 3 === 2 ? alpha(K.cyan, 0.3) : alpha(K.cyan, 0.65)); }
  px(g, sx - 8, 8, 17, 1, K.white);
  px(g, sx - 1, 15, 3, 9, K.white); px(g, sx - 4, 18, 9, 3, K.white); // cross mark on the badge
  px(g, bx + w + 4, 32, 1, 8, alpha(K.cyan, 0.5)); // projector beam
  // enlarged 1.5x (nearest neighbour) so the sign reads
  const S = 1.5, [big, bg] = canvas(Math.ceil(96 * S), Math.ceil(100 * S));
  bg.imageSmoothingEnabled = false; bg.drawImage(cv, 0, 0, 96 * S, 100 * S);
  return { cv: big, ax: ax * S, ay: ay * S };
}
function cone() {
  const [cv, g] = canvas(14, 20), ax = 7, ay = 19;
  px(g, 0, 16, 14, 3, K.ink); px(g, 1, 16, 12, 2, K.metalLo);
  for (let j = 0; j < 15; j++) { const hw = 1 + Math.floor(j / 2.4); px(g, 7 - hw - 1, 1 + j, hw * 2 + 3, 1, K.ink); px(g, 7 - hw, 1 + j, hw * 2 + 1, 1, K.warm); }
  px(g, 5, 7, 5, 2, K.white); px(g, 4, 11, 7, 2, K.white);
  return { cv, ax, ay };
}
function tree(v: number) {
  const [cv, g] = canvas(48, 72), ax = 24, ay = 70;
  px(g, ax - 2, 40, 5, ay - 40, K.ink); px(g, ax - 1, 40, 2, ay - 42, K.woodLo); px(g, ax - 1, 40, 1, ay - 42, K.wood);
  const blobs = [[24, 26, 18, 16], [14, 32, 11, 9], [34, 30, 12, 10], [24, 16, 13, 11]];
  for (const [bx, by, rx, ry] of blobs) disc(g, bx, by, rx + 1, ry + 1, K.ink);
  for (const [bx, by, rx, ry] of blobs) disc(g, bx, by, rx, ry, K.leafLo);
  for (const [bx, by, rx, ry] of blobs) disc(g, bx - 3, by - 3, rx - 5, ry - 5, K.leaf);
  for (let k = 0; k < 18; k++) px(g, 8 + Math.floor(hash2(k, v * 99) * 26), 8 + Math.floor(hash2(v * 77, k) * 22), 2, 2, K.leafHi);
  return { cv, ax, ay };
}
